import React, { FC, useCallback } from 'react';

import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import ItemActions from 'components/item-actions';
import { ListItemGroup } from 'components/list-item-group';
import useFoodware from 'hooks/use-foodware';
import { isCartEntryReorderable } from 'hooks/use-reorderable-orders/utils';
import { IReorderEntry } from 'pages/menu/hooks/types';
import { useReorderEntries } from 'pages/menu/hooks/use-reorder-entries';
import { RecentOrderCard } from 'pages/menu/recent-items-page/recent-order-card-list/recent-order-card-list-item';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { useReorder } from 'state/order/hooks/use-reorder';
import { routes } from 'utils/routing';

import { IFavoriteCardProps } from './types';

export const FavoriteCard: FC<React.PropsWithChildren<IFavoriteCardProps>> = ({
  id,
  entries,
  name,
  onEditFavorite,
  onRemoveFavorite,
}) => {
  const favoriteOrder = {
    cart: {
      cartEntries: entries,
      serviceMode: null,
    },
  };

  const { formatMessage } = useIntl();

  const { handleReorderClick } = useReorder(favoriteOrder, routes.menu);
  const { reorderEntriesMap, checkingAvailability, hasSomeAvailableEntry } = useReorderEntries(
    favoriteOrder?.cart.cartEntries
  );

  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const { isFoodware } = useFoodware();

  const handleReorder = useCallback(() => {
    for (const entry of favoriteOrder.cart.cartEntries) {
      logEvent(CustomEventNames.ADD_FAVORITE_TO_CART, EventTypes.UserContent, {
        'Product Name': entry?.name,
        'User Location': 'Favorites Tab',
      });
    }
    handleReorderClick();
  }, [favoriteOrder.cart.cartEntries, handleReorderClick]);

  if (!entries.length) {
    return null;
  }

  const recentOrderCardsFromFavorites = entries.reduce((acc: React.ReactNode[], entry) => {
    if (!isCartEntryReorderable(entry, sanityIdsExcludedFromReordering, isFoodware)) {
      return acc;
    }

    acc.push(
      <RecentOrderCard
        key={entry?.lineId}
        cartEntry={entry}
        mappedEntry={reorderEntriesMap[entry.lineId] ?? ({} as IReorderEntry)}
        checkingAvailability={checkingAvailability}
        isOnFavoriteSection
      />
    );

    return acc;
  }, []);

  return (
    <ListItemGroup
      header={name || ''}
      headerAction={
        <ActionButton
          testID="reorder-button"
          onPress={handleReorder}
          disabled={!hasSomeAvailableEntry}
          size={ActionButtonSizes.SMALL}
        >
          {formatMessage({ id: 'orderNow' })}
        </ActionButton>
      }
      items={recentOrderCardsFromFavorites}
      footer={
        <ItemActions
          onEdit={() => onEditFavorite(id ?? '')}
          onRemove={() => onRemoveFavorite(id ?? '')}
        />
      }
      key={id}
    />
  );
};
